import { defineStore } from 'pinia';
import { ref } from 'vue';

export type NotificationLevel = 'info' | 'success' | 'warning' | 'error';

export interface Notification {
  id: number;
  level: NotificationLevel;
  title: string;
  message?: string;
  /** Auto-Dismiss in ms, 0 = bleibt bis User klickt */
  ttlMs: number;
  createdAt: number;
}

let nextId = 1;

/**
 * Globale Toast-/Notification-Verwaltung. Pages und API-Calls können
 * via {@code notify.success(...)}, {@code notify.error(...)} etc. einen
 * neuen Toast pushen — die App.vue rendert sie als Stack rechts oben.
 */
export const useNotificationsStore = defineStore('notifications', () => {
  const items = ref<Notification[]>([]);

  function push(level: NotificationLevel, title: string,
                message?: string, ttlMs = 5000) {
    const n: Notification = {
      id: nextId++, level, title, message,
      ttlMs, createdAt: Date.now(),
    };
    items.value.push(n);
    if (ttlMs > 0) {
      setTimeout(() => dismiss(n.id), ttlMs);
    }
    return n.id;
  }

  function dismiss(id: number) {
    items.value = items.value.filter((n) => n.id !== id);
  }

  function clear() { items.value = []; }

  return {
    items,
    push,
    dismiss,
    clear,
    info:    (title: string, msg?: string, ttl?: number) => push('info',    title, msg, ttl),
    success: (title: string, msg?: string, ttl?: number) => push('success', title, msg, ttl),
    warning: (title: string, msg?: string, ttl?: number) => push('warning', title, msg, ttl ?? 7000),
    error:   (title: string, msg?: string, ttl?: number) => push('error',   title, msg, ttl ?? 0),
  };
});
